/**
 * Feature flags. Set to true when the feature is ready; keep false to disable until fixed.
 */

/**
 * Free trial auto-start on first access. Env-driven, DEFAULT FALSE — only `=true` enables it.
 * Go-live / rollback is just an env change + restart (no code change).
 */
export const FREE_TRIAL_START_ENABLED = (process.env.FREE_TRIAL_START_ENABLED ?? 'false') === 'true'

/**
 * Card-on-file Stripe free trial, in days. DISTINCT from FREE_TRIAL_START_ENABLED above: that grants
 * in-app premium DAYS with NO card; this attaches a real Stripe `trial_period_days` to the
 * subscription checkout, so the customer enters a card, is charged nothing today, and is billed only
 * if they do not cancel before the trial ends. This is the single most-validated subscription
 * conversion lever — it removes the pay-now wall at the exact step abandoners quit (register:
 * `card-on-file-trial-0822`).
 *
 * SERVER-ONLY (no NEXT_PUBLIC_ prefix): the client learns trial length + eligibility from
 * /api/user/subscription-status, never by importing this. `0` (or unset-to-default) length disables
 * it — go-live / instant rollback is an env change + restart, no redeploy. Default 7.
 */
export const STRIPE_TRIAL_DAYS = Math.max(0, Math.floor(Number(process.env.STRIPE_TRIAL_DAYS ?? '7')) || 0)

/**
 * ACTIVATION EMAIL for never-tasted registrants (return-nudge-coverage-and-activation-email-1008). The
 * week-1 return-nudge only reaches users who TASTED premium; ~26/mo registrants got free days and never
 * used them, with no email reach at all. This is the email that pulls them in. PRE-BUILT DARK: default
 * false, and email-activation-nudge.ts refuses to --send while it is off, so it is inert until armed. Flip
 * this true (env) + add the cron + canary post-10-11, so it never contaminates the recovery read.
 */
export const ACTIVATION_NUDGE_ENABLED = (process.env.ACTIVATION_NUDGE_ENABLED ?? 'false') === 'true'

/**
 * TASTING-SLICE A/B (register: tasting-premium-is-the-real-lever-0819 + dose-curve-is-interaction-not-taste-depth-1017).
 * Pre-payment tasting predicts paying, but it is an INTERACTION with RETURN-FREQUENCY, not a taste-depth effect:
 * controlling for pre-pay return-days, frequent returners who never taste pay 0 (0 of 189), and depth past ~2-3
 * tastes adds little. Each taste BURNS a grant-day, so reaching the dose exhausts the finite 6-day grant. When ON, the 'treatment' arm
 * (tasteSliceArm, 50/50 by user id - tasteSliceHoldout.ts) does NOT burn a grant-day on access during the
 * account's first TASTE_SLICE_WINDOW_DAYS, so a new user can taste repeatedly without spending their trial
 * and keeps the grant as upgrade runway. OFF (default) = burn as today, byte-identical. Judge per-arm on
 * PAYING at 30d vs the control holdout, NEVER on taste count; SEGMENT the read by pre-pay return-days. Instant rollback: env + restart.
 * ⛔ Do NOT run concurrently with FREE_DAYS_SHRINK_AB (both change grant economics and would confound).
 */
export const TASTE_SLICE_ENABLED = (process.env.TASTE_SLICE_ENABLED ?? 'false') === 'true'
/** How many days after signup the treatment arm's tasting is free of grant-burn. Default 7 (covers ~5
 *  trading days, enough to return and taste across several days). Env-tunable; floored at 1. */
export const TASTE_SLICE_WINDOW_DAYS = Math.max(1, Math.floor(Number(process.env.TASTE_SLICE_WINDOW_DAYS ?? '7')) || 7)

/**
 * TRIAL-END RECAP emails (register: trial-drift-and-dead-grant-cliff-email-1016). SHIPS DARK. When a user's
 * free premium days run out, 81% go silent and NOTHING reached them (send-reengagement selects a column
 * nothing writes and has no timer). This sends two notes from src/scripts/email-trial-recap.ts: a "1 day
 * left" note when the balance reaches 1, and "your premium week, recapped" when it reaches 0, showing what
 * the scanner flagged during that user's own days. OFF (default) = the script refuses --send. Judge on
 * PAYING within 14 days of the cliff vs the TRIAL_RECAP_HOLDOUT_PCT holdout, stratified by tasting-slice arm.
 */
export const TRIAL_RECAP_ENABLED = (process.env.TRIAL_RECAP_ENABLED ?? 'false') === 'true'

/**
 * NO-CARD trial checkout (checkout-no-card-trial-0917). The card-on-file trial above still shows a CARD
 * WALL: `payment_method_collection` defaults to 'always', so the customer must enter a card for a $0
 * trial - the exact step 57% of checkouts abandon (measured: 47 of 78 starts in 45d were $0 trial
 * sessions, most expiring at the card page). When ON, the trial checkout uses 'if_required', so a $0
 * trial collects NO card up front; the existing trial_settings.end_behavior cancels cleanly at trial end
 * if none is ever added (no surprise charge). Only affects TRIAL checkouts - a checkout with a payment
 * due today (no trial, or a discounted-but-nonzero first invoice) still collects the card, because
 * 'if_required' means "collect when a payment is actually needed now". Instant rollback: env + restart.
 */
export const CHECKOUT_NO_CARD_TRIAL = (process.env.CHECKOUT_NO_CARD_TRIAL ?? 'false') === 'true'

/**
 * TRIAL CARD A/B (trial-conversion-card-capture-0921). No-card trials start more people but 1/3 carry no
 * card and cannot auto-convert; the card-on-file trial is our best converter (28 of 46 subs ever). This
 * splits new trial checkouts card-required vs no-card to read which nets more PAID. FALSE (default) keeps
 * the current global CHECKOUT_NO_CARD_TRIAL behavior for everyone, so it ships dark. TRIAL_CARD_AB_PCT is
 * the fraction that gets CARD-required (see trialCardHoldout.ts). ⚠ Enable AFTER the 09-24 pass read so the
 * two checkout experiments do not confound each other; run with a holdout and read per-day.
 */
export const TRIAL_CARD_AB_ENABLED = (process.env.TRIAL_CARD_AB_ENABLED ?? 'false') === 'true'

/**
 * Trial card-reuse guard (register: trial-card-reuse-one-card-four-trials-1031). A trial started on a card
 * another Stripe customer already subscribed with is ended at once: charged now, or cancelled on a
 * decline (trialCardReuse.ts). OFF BY DEFAULT: it charges real cards at checkout, so it ships dark, the
 * backfill is run and the table read, and only then is it armed deliberately with
 * TRIAL_CARD_REUSE_GUARD_ENABLED=true. Cards are recorded either way, so arming later loses no history.
 */
export const TRIAL_CARD_REUSE_GUARD_ENABLED = (process.env.TRIAL_CARD_REUSE_GUARD_ENABLED ?? 'false') === 'true'

/**
 * CHARGE NON-US VISITORS AT CHECKOUT (first-charge-declines-lose-a-third-of-payers-1017). Trials that reach
 * the first charge: US cards pay 9 of 9, non-US 8 of 15. When on, a trial-eligible first-timer whose browser
 * time zone is outside the US is split 50/50: half pay at checkout (on-session, so 3DS and a retry with
 * another card can happen), half keep the free week as the holdout (nonUsChargeNow.ts). US and unknown time
 * zones are never touched. OFF BY DEFAULT: it removes a free week, so arming is the owner's call.
 */
export const NONUS_CHARGE_AT_CHECKOUT_ENABLED = (process.env.NONUS_CHARGE_AT_CHECKOUT_ENABLED ?? 'false') === 'true'

/**
 * EMBEDDED CHECKOUT A/B (register: embedded-checkout-keeps-them-on-site-1020). When on, EMBEDDED_CHECKOUT_PCT
 * (default 50) of /subscribe checkouts open Stripe's form INSIDE the page instead of redirecting to the
 * Stripe-hosted page, where about half arrive and leave without typing anything. See embeddedCheckout.ts.
 * ⛔ OFF BY DEFAULT, and inert until BOTH this AND STRIPE_PUBLISHABLE_KEY (the public pk_live_ key) are set.
 * ⭐ NOT A CONST ANY MORE: both are read at request time via getSetting/getBoolSetting (settings/settingsStore.ts),
 * so the OWNER sets them on /admin/integrations with no deploy; .env.local still works as the fallback. Rollback:
 * switch it off on that page.
 */

/**
 * Free-days grant shrink A/B (register: free-days-grant-shrink-ab-prescope-1004). SHIPS DARK.
 * When ON, the 'short' arm (freeDaysShrinkArm, 50/50 by user id) gets FREE_DAYS_SHRINK_DAYS free premium
 * days at signup instead of the full PRICING.trialTradingDays, to test whether a shorter runway nets MORE
 * paid conversions (urgency) or fewer (less engagement runway). OFF (default) = everyone gets the full
 * grant, zero behavior change. Consumed only in grantTrialDays (premiumDays.ts).
 * RUN AFTER the no-card recovery (no-card-trial-zeroed-new-payers-1004) so the baseline is clean; read net
 * payers AND day-2 return per arm. NEVER inline-comment this on the VPS .env.local (systemd keeps the
 * comment in the value - systemd_envfile_keeps_inline_comments); verify via /proc.
 */
export const FREE_DAYS_SHRINK_AB_ENABLED = (process.env.FREE_DAYS_SHRINK_AB_ENABLED ?? 'false') === 'true'
export const FREE_DAYS_SHRINK_DAYS = Math.max(1, Math.floor(Number(process.env.FREE_DAYS_SHRINK_DAYS ?? '3')) || 3)

/**
 * Onboarding SEED-MIX A/B (register: onboarding-seed-megacaps-vs-live-movers-1005). SHIPS DARK.
 * When ON, the 'treatment' arm (seedMixArm, 50/50 by user id) gets a new user's first watchlist seeded
 * with SUSTAINED LOW-FLOAT movers (validated: low-float names that already alerted 3+ of the last 5 days
 * keep alerting 82% vs 44% for a one-day spike) instead of static mega-caps, to test the repeat-tasting
 * dose curve (one-and-done trial users convert 0%, 5+-day returners 28%). OFF (default) = everyone gets
 * the existing static mega-cap seed, zero behavior change. Consumed via getOnboardingSeedPool (seedMix.ts).
 * ⛔ GATED: enable ONLY after the no-card recovery read lands clean (~2026-10-13), so the seed-mix signal
 * is not confounded by the payer-recovery lag. NEVER inline-comment this on the VPS .env.local (systemd
 * keeps the comment in the value - systemd_envfile_keeps_inline_comments); verify via /proc.
 */
export const SEED_MIX_ENABLED = (process.env.SEED_MIX_ENABLED ?? 'false') === 'true'

/**
 * SCANNER-LANDER WELCOME A/B (register: scanner-landers-pay-zero-and-mix-shift-1017). Signups whose first
 * page was a free scanner page (/rvol-scanner, /halt-scanner, /screener...) paid 0 of 122 vs 5.0% for
 * everyone else; they taste premium at the same rate but do not come back. TREATMENT (50%, hash-split,
 * independent of seed-mix) sees /welcome lead with what premium adds to the scanner they came for. OFF
 * (default) = /welcome byte-identical. ⛔ GATED: enable ONLY after the 2026-10-25 seed-mix read, so the
 * two /welcome experiments do not stack on one population. Never inline-comment it in .env.local.
 */
export const SCANNER_WELCOME_ENABLED = (process.env.SCANNER_WELCOME_ENABLED ?? 'false') === 'true'

/**
 * NEW-PAYER HOW-TO email (register: churn-signal-site-too-complicated-1011; owner chose it 2026-10-17). One
 * plain "three things to set up, and how not to use it" email to each person who just started premium.
 * Read by the CRON SCRIPT (src/scripts/email-payer-howto.ts), not the web app: --send refuses while this
 * is off. OFF (default) = nothing sends. Never inline-comment it in .env.local.
 */
export const PAYER_HOWTO_ENABLED = (process.env.PAYER_HOWTO_ENABLED ?? 'false') === 'true'

/**
 * MORNING BRIEF INVITE RCT (register: brief-invite-rct-1018). One product-update email inviting non-paying,
 * not-new users to the free daily Morning Brief, 50/50 hash holdout, to answer the open question in
 * returning-is-the-gate-not-verification: does the brief CAUSE people to come back? Read by the cron SCRIPT
 * (src/scripts/email-brief-invite.ts): --send refuses while off. Never inline-comment it in .env.local.
 */
export const BRIEF_INVITE_ENABLED = (process.env.BRIEF_INVITE_ENABLED ?? 'false') === 'true'

/**
 * "Sell time" delay A/B (register: sell-time-delay-model-scope-1004). SHIPS DARK.
 * When ON, free-tier (trial-expired) users in the 'delayed' arm (sellTimeDelayArm, 50/50) see the alert feed
 * delayed by SELL_TIME_DELAY_MINUTES; subscribers and in-trial users always get real-time. This is the
 * ✅ CONSUMERS BUILT 2026-10-19 (owner: build dark, the delayed feed REPLACES the 15-min free window on /alerts):
 * one decision (src/lib/premium/sellTimeDelay.ts, excludes comp accounts + unverified) used by /api/ws-ticket
 * (signed `d`), the WS server (holds NEW_ALERT in DelayedReleaseQueue, filters INITIAL_STATE), /recent-live +
 * /recent (time cap, cache bypass, RTH stale filter skipped), subscription-status (`delayedFeed`), and the
 * /alerts gate + per-alert "members saw this at" stamp. WS_TICKET_ENFORCED=true on prod (verified 10-19).
 * OFF (default) = no one delayed, zero behavior change.
 * ⛔ ARMING NEEDS THREE PROCESSES TO SEE IT: set it in .env.local, then DEPLOY (web app reads env at start)
 * AND `systemctl restart thedesperatetrader-websocket` (a deploy does NOT restart that unit). The WS server
 * ignores ticket `d` unless its OWN env has this flag. Owner sequencing: arm after the 2026-10-25 read.
 * NEVER inline-comment on the VPS .env.local (systemd keeps the comment); verify via /proc.
 */
export const SELL_TIME_DELAY_AB_ENABLED = (process.env.SELL_TIME_DELAY_AB_ENABLED ?? 'false') === 'true'
export const SELL_TIME_DELAY_MINUTES = Math.max(1, Math.floor(Number(process.env.SELL_TIME_DELAY_MINUTES ?? '20')) || 20)

/**
 * When false: no subscription/daily-limit gating. Everyone gets full access; no "Daily limit reached"
 * or subscribe prompts. Env-driven, DEFAULT FALSE — only `SUBSCRIPTION_GATING_ENABLED=true` flips
 * it on. Go-live / instant rollback is just an env change + restart (no code change, no redeploy).
 */
export const SUBSCRIPTION_GATING_ENABLED = (process.env.SUBSCRIPTION_GATING_ENABLED ?? 'false') === 'true'

/**
 * WIN-RECAP ASK ("ask at the win", register: ask-at-the-win-scope-0905). Shows a non-subscriber a
 * personalized ask when their OWN alerted symbol ran up (precomputed by build-win-recaps.ts into
 * win_recaps). Env-gated, DEFAULT FALSE - while false the normal low_balance/out_of_days ask fires
 * unchanged. Measured by CLICK RATE vs the generic friction ask, NOT conversion (conversion is
 * unreadable at ~10 conversions/cohort - see mid-taste-moment-design-0904).
 *   WIN_RECAP_ASK_ENABLED  turns it on.
 *   WIN_RECAP_ASK_PCT      A/B treatment fraction 0..1 (default 0.5): the rest are the control and keep
 *                          the generic ask, so the click-rate comparison is clean.
 */
export const WIN_RECAP_ASK_ENABLED = (process.env.WIN_RECAP_ASK_ENABLED ?? 'false') === 'true'
export const WIN_RECAP_ASK_PCT = Math.min(1, Math.max(0, Number(process.env.WIN_RECAP_ASK_PCT ?? 0.5)))

/**
 * Dev-only ASK PREVIEW route (/dev/ask-preview). DEFAULT FALSE. When true, the route renders the REAL ask
 * cards (via the real premiumBannerState + PremiumInlineAsk) from MOCK inputs, so any ask state -
 * win_recap, low_balance, out_of_days, the service bars - can be browser-verified in isolation with no DB,
 * no session, and no eligible-user state to reproduce. It renders MOCK UI only (no real user data), so
 * enabling it exposes nothing sensitive; it stays off by default so a /dev route is never public unless
 * someone opts in. Fills the "show a preview first" render gap (2026-09-06).
 */
export const DEV_ASK_PREVIEW_ENABLED = (process.env.DEV_ASK_PREVIEW_ENABLED ?? 'false') === 'true'

/**
 * ONE-CLICK SIGN-IN LINK in conversion emails (register: one-click-signin-email-conversion-0906). DEFAULT
 * FALSE (dormant). When true, the conversion senders (send-conversion-offer, send-reactivation) swap their
 * plain CTA for a signed, TTL'd, single-use signInUrl(), so a returning recipient lands authenticated on
 * the target page instead of hitting the /auth wall. The `signin-token` NextAuth provider and /signin route
 * are ALWAYS live (a forged/expired/consumed token simply fails to sign anyone in); this flag only gates
 * whether real emails USE the link, so the flow can be verified end-to-end before any campaign relies on it.
 */
export const SIGNIN_LINK_ENABLED = (process.env.SIGNIN_LINK_ENABLED ?? 'false') === 'true'

/**
 * PER-TICKER DISCUSSION on /stocks/[symbol] (register: community-shape-from-reddit-0906). DEFAULT FALSE.
 * When true, the ticker hub mounts the generic GuideComments thread keyed by (entityType:'ticker',
 * entityId:SYMBOL), reusing the proven held-for-approval comments pipeline (the #1 trader behavior per
 * the Reddit mine: discuss why a stock is moving). v1 is founder-moderated (held-for-approval, SEO-safe
 * because the list is client-fetched so no unreviewed text reaches the crawled HTML). A live/immediate
 * model for signed-in subscribers is the scoped v2, not this flag.
 */
export const TICKER_DISCUSSION_ENABLED = (process.env.TICKER_DISCUSSION_ENABLED ?? 'false') === 'true'

/**
 * FOUNDING-MEMBER IDENTITY (register: founding-member-identity-0906, mission layer). DEFAULT FALSE.
 * When true, /subscribe shows the founding-member social proof + mission ("join N founding members
 * backing a trader-owned platform; your rate is locked"), and a founding subscriber sees their badge.
 * The rate-lock claim is honest by Stripe default (existing subscriptions are never auto-raised). See
 * src/lib/premium/foundingMember.ts.
 */
export const FOUNDING_MEMBER_ENABLED = (process.env.FOUNDING_MEMBER_ENABLED ?? 'false') === 'true'

/**
 * INLINE TRACK-RECORD PROOF on /subscribe (register: subscribe-inline-proof-0906). DEFAULT FALSE.
 * Today the /subscribe proof is a bare LINK to /track-record — the skeptical trader must leave the
 * decision moment to see whether it works, then come back to buy. When true, /subscribe renders a compact,
 * honest AGGREGATE proof card INLINE (the validated "{N}× more likely to move +5% within the hour" claim,
 * its random-bar baseline, the sample size, and ALWAYS the downside beside the upside), read from the SAME
 * track_record_snapshot the full page uses so the two can never drift. Distinct from the existing
 * SubscribeProof "recently caught early" receipt cards (specific dated wins) — this is the statistical
 * edge. While false the bare link is shown unchanged, so it can be browser-verified behind the flag first.
 */
export const SUBSCRIBE_PROOF_ENABLED = (process.env.SUBSCRIBE_PROOF_ENABLED ?? 'false') === 'true'

/**
 * Track-record PROOF on the SIGNED-OUT /alerts gate (register: gate-proof-holdout-alerts-0930). The gate is
 * arguably the highest-intent conversion moment (a visitor came specifically for live alerts) yet carries no
 * product proof. When true, signed-out /alerts visitors are split 50/50 by a dt_gpa cookie (assigned server-
 * side in /api/track-record/proof): treatment sees the compact TrackRecordProofCard figures above the gate,
 * control sees the current bare gate. The arm is stamped on the account at signup (via /welcome) so the
 * signup lift is read treatment vs control - ask-events are authed-only, so a cookie-arm is the signed-out
 * measurement path. FALSE (default) = nobody sees it and no arm is assigned, so it ships dark.
 */
export const GATE_PROOF_ENABLED = (process.env.GATE_PROOF_ENABLED ?? 'false') === 'true'

/**
 * /top-stocks CONVICTION RE-RANK holdout (register: conviction-rank-holdout-topstocks-1005). /top-stocks
 * ranks its six cards by BOARD CONFLUENCE (how many scanners agree = attention, not likelihood). The
 * conviction signal (float x not-extended) is validated OOS and TEMPORALLY to order names by the
 * probability of a clean +5%/60m run (A beats base 14/14 and 6/6 days; see alertConviction.ts). When true,
 * /top-stocks visitors are split 50/50 by a dt_cvr cookie (assigned in middleware so the SSR first paint is
 * a stable arm): TREATMENT sees the cards re-ordered by conviction score, CONTROL sees today's confluence
 * order. /stock-market-today and the shared poll endpoints are untouched. The arm is stamped on the account
 * at signup so the signup lift is read treatment vs control (the anon measurement path, like gate-proof).
 * FALSE (default) = everyone sees control, so it ships dark and can be browser-verified behind the flag.
 */
export const CONVICTION_RANK_ENABLED = (process.env.CONVICTION_RANK_ENABLED ?? 'false') === 'true'

/**
 * PER-NAME CONVICTION on /pulse (register: next-level-rank-by-proven-likelihood-not-attention-1004,
 * Piece 3 - the live conviction feed for subscribers). When true, the /pulse watchlist and trending
 * chips carry a compact conviction badge (tier + the clean-run % AND the drop % together) for any name
 * currently scannable at tier A/B/C, read from the SAME scoreConviction rates /best-bets uses. The score
 * is joined server-side off a cached scored set (convictionBySymbol.ts), so no per-symbol DB read hits the
 * hot path. FALSE (default) = the page computes no conviction and the chips render exactly as today, so it
 * ships dark and can be browser-verified behind the flag before the owner arms it on a closed market.
 */
export const PULSE_CONVICTION_ENABLED = (process.env.PULSE_CONVICTION_ENABLED ?? 'false') === 'true'

/**
 * PER-NAME CONVICTION on /radar (register: next-level-rank-by-proven-likelihood-not-attention-1004,
 * Piece 3). /radar, the customer-facing Watcher board, labels names by ATTENTION (how many signals agree)
 * with no LIKELIHOOD dimension. When true, each radar row carries the conviction badge (tier + clean-run %
 * AND drop %), scored directly from live data via scoreSymbolsConviction and filtered to tier A/B/C. FALSE
 * (default) = the board computes no conviction and renders exactly as today, so it ships dark and can be
 * browser-verified behind the flag. Arm only after the /pulse pilot validates the badge design, so the
 * copy/layout is locked before it lands on a second surface.
 */
export const RADAR_CONVICTION_ENABLED = (process.env.RADAR_CONVICTION_ENABLED ?? 'false') === 'true'

/**
 * THE READ - the /pulse hero card (register: next-level-rank-by-proven-likelihood-not-attention-1004,
 * Piece 3). Synthesises the single highest-conviction live name into one card: the guarded move, the tier,
 * BOTH sides of the odds, why it is moving, and how to hold it. It reads ONLY the trending + watchlist rows
 * and the cached conviction map /pulse already computes (pickTheRead in theRead.ts), so it adds no query.
 * FALSE (default) = /pulse renders exactly as today (no hero), so it ships dark and can be browser-verified
 * behind the flag before the owner arms it. When on, the page computes the conviction map even if
 * PULSE_CONVICTION_ENABLED is off, because the hero needs it.
 */
export const THE_READ_ENABLED = (process.env.THE_READ_ENABLED ?? 'false') === 'true'

/**
 * THE PRECEDENT READ on /stocks/[symbol] (register: precedent-read-float-matched-forward-distribution-
 * validated-1102). A per-name card showing the measured 60-minute forward-EXCURSION distribution of past
 * moves in setups that profile like this one (float-matched via the live scoreConviction tier): how high
 * they reached and how far they gave back, two-sided, with tier A leading on the validated downside edge
 * (collapses less than other micro-float movers). Numbers are a point-in-time calibration in
 * precedentCohorts.ts, measured read-only on bar_features over 75 sessions. The card is a client island
 * fed by /api/stocks/precedent, which returns null for every name while this is false, so the page is
 * byte-identical until armed. FALSE (default) = ships dark, browser-verifiable behind the flag before the
 * owner arms it. ⚠ Still owed before arming: a per-week stability read on the percentile shape (the 63/75
 * per-day sign count already covers the tier-A downside claim). Never inline-comment it in .env.local.
 */
export const PRECEDENT_READ_ENABLED = (process.env.PRECEDENT_READ_ENABLED ?? 'false') === 'true'

/**
 * TOP-CONVICTION STRIP on /alerts (register: precedent-read-float-matched-forward-distribution-validated-
 * 1102, the /alerts importance signal). The firehose is newest-first with no "which of these matters"
 * signal - two non-subscribers named this ([[request-alert-filtering-demand]]). This surfaces the current
 * highest-conviction names (ranked A>B>C off the SAME cached scored set /pulse uses) in a compact strip at
 * the top of the feed, each linking to its /stocks page (where the full Precedent panel lives).
 *
 * ⚠ GATED SERVER-SIDE, NOT in the client page: /alerts is a client component, so a non-NEXT_PUBLIC flag
 * reads undefined in the browser and would be stuck dark. Instead /api/alerts/top-conviction returns an
 * empty list while this is false, and the client strip hides on empty - so the flag (read server-side in
 * the route) is the real gate. FALSE (default) = the API returns nothing and the strip never shows, so it
 * ships dark. Never inline-comment it in .env.local.
 */
export const ALERTS_TOP_CONVICTION_ENABLED = (process.env.ALERTS_TOP_CONVICTION_ENABLED ?? 'false') === 'true'

/**
 * CONVICTION_TOPPICK_MODE - the shared homepage/hero Top Pick re-rank (conviction-toppick-rollout-boundary-1021).
 * 'off'    (default) confluence picks the hero, unchanged.
 * 'shadow' confluence STILL serves, but during RTH we also compute the conviction pick and log the pair so
 *          rank quality can be validated on THIS population (board names scored at now) for ~2 weeks before
 *          any user-facing change. Zero user impact.
 * 'on'     the best tier-A/B conviction pick among the confluence candidates becomes the hero (both the
 *          homepage chip and /stock-market-today, since the pick is shared). This mode is the before/after
 *          boundary; do not set it until the shadow validates and the /top-stocks A/B is formally closed.
 */
const _topPickMode = (process.env.CONVICTION_TOPPICK_MODE ?? 'off').toLowerCase()
export const CONVICTION_TOPPICK_MODE: 'off' | 'shadow' | 'on' =
  _topPickMode === 'on' || _topPickMode === 'shadow' ? _topPickMode : 'off'

/**
 * The checkout balk-fork (willingness-to-pay-confirmed-at-checkout-0918): when a treatment-arm, logged-in
 * non-subscriber clicks the monthly $24.99 CTA, offer the lower rung (a $4.99 day / $9.99 week pass) AT the
 * click instead of below the fold, with "Continue to $24.99/mo" as the obvious default. FALSE (default) =
 * today's straight-to-Stripe behaviour for everyone, so it ships dark and can be browser-verified behind
 * the flag. The A/B holdout is a SECOND knob (BALK_FORK_HOLDOUT_PCT, see balkForkHoldout.ts): even with the
 * fork enabled, that fraction of users is control (straight to Stripe) so we read total-conversion lift vs
 * monthly dilution. ⚠ Hold the enable until AFTER the pass-visibility read (2026-09-24) so the two offer
 * changes on this surface do not confound each other.
 */
export const BALK_FORK_ENABLED = (process.env.BALK_FORK_ENABLED ?? 'false') === 'true'

/**
 * Show the CARD-ON-FILE TRIAL on /subscribe (two-free-offers-only-one-converts-0920). While false the
 * page is unchanged, so it can be browser-verified behind the flag first.
 *
 * ⚠ Turning this ON starts an A/B, it does not ship to everyone: SUBSCRIBE_TRIAL_HOLDOUT_PCT defaults
 * to 50, so half of logged-in visitors are the control. That default is deliberate - see
 * subscribeTrialHoldout.ts. Set the pct to 0 only if you consciously want an unmeasurable 100% ship.
 */
export const SUBSCRIBE_TRIAL_COPY_ENABLED = (process.env.SUBSCRIBE_TRIAL_COPY_ENABLED ?? 'false') === 'true'

/**
 * ONE-OFFER /subscribe layout A/B (register: subscribe-page-one-offer-told-five-ways-1031). Signed-in
 * visitors split 50/50 by user id (subscribeLayoutHoldout.ts); the treatment drops the server shell's
 * price restatements and plan grid so the client card is the one place the offer is stated. Logged-out
 * visitors and crawlers always get today's page. ⚠ Turning this ON starts the A/B; admins can preview
 * either arm with ?layout=oneoffer|control while it is off.
 */
export const SUBSCRIBE_ONE_OFFER_ENABLED = (process.env.SUBSCRIBE_ONE_OFFER_ENABLED ?? 'false') === 'true'

/**
 * The first-session return hook (register: first-session-return-hook-0927): a one-tap "set your watchlist,
 * we'll flag these for you at the next open" card for a BRAND-NEW user with an empty watchlist, aimed at the
 * measured day-1 -> day-2 activation cliff ([[activation-cliff-is-day1-to-day2-0927]]). FALSE (default) =
 * nobody sees it, so it ships dark and can be browser-verified behind the flag (admins can force it with
 * ?fsh_preview=1). When true, firstSessionHookArm() splits new users 50/50: treatment-eligible see the card,
 * control never does, so the day-2-return lift is a clean intent-to-treat read. Pure watchlist utility, no
 * money ask, so it does not spend the subscriber-quiet promise and is exempt from the 48h new-account quiet rule.
 */
export const FIRST_SESSION_HOOK_ENABLED = (process.env.FIRST_SESSION_HOOK_ENABLED ?? 'false') === 'true'

/**
 * The first-week plan (register: first-week-plan-1030): one small step per visit on days 2-7 of a new
 * account, each pointing at one page with the site guide's own "what it is / how to use it". Day 1 stays
 * with the welcome checklist and the first-session hook. FALSE (default) = nobody sees it; admins can force
 * it with ?fw_preview=1 (and &fw_day=N). When true, firstWeekArm() splits accounts 50/50 by user id.
 * ⛔ Arm only AFTER the live new-user reads land (tasting-slice 11-16): a fourth concurrent new-user arm on
 * ~10 signups a day leaves none of them readable (aha-path-return-and-depth-onboard-test-1016).
 */
export const FIRST_WEEK_ENABLED = (process.env.FIRST_WEEK_ENABLED ?? 'false') === 'true'
/** Only accounts created on or after this ET date (yyyy-MM-dd) are enrolled, so the read starts from a
 *  clean boundary instead of catching people mid-week. Empty = no boundary. Set it to the arming date. */
export const FIRST_WEEK_ENROLL_FROM = (process.env.FIRST_WEEK_ENROLL_FROM ?? '').trim()

/**
 * The peak-desire trial trigger (register: funnel-entry-is-the-bottleneck-not-close-0928). The paid funnel
 * CLOSES at ~79% but only ~4.6% of engaged users ever ENTER it, so the lever is getting engaged free users
 * INTO the trial at a moment of maximum desire: right after a stock they were watching was flagged and ran.
 * FALSE (default) = nobody sees it, ships dark and admin-previewable with ?pdt_preview=1. When true,
 * peakDesireTrialArm() splits eligible users 50/50 (control never sees it) so the funnel-entry lift is a
 * clean intent-to-treat read. It IS a money ask, so it gates on non-subscriber + the 48h quiet window +
 * trial eligibility, and only fires when the user has a REAL missed mover to show.
 */
export const PEAK_DESIRE_TRIAL_ENABLED = (process.env.PEAK_DESIRE_TRIAL_ENABLED ?? 'false') === 'true'

/**
 * The first proof-in-the-path A/B (welcome-proof-card-ab-spec-0909): the honest Engine-A track-record card
 * on /welcome, the highest-intent post-signup moment. FALSE (default) = the current no-proof welcome for
 * everyone, so it ships dark and can be browser-verified behind the flag. When true, the welcome-proof
 * holdout (WELCOME_PROOF_HOLDOUT_PCT) splits treatment (card) vs control (current welcome, no proof).
 */
export const WELCOME_PROOF_ENABLED = (process.env.WELCOME_PROOF_ENABLED ?? 'false') === 'true'

/**
 * Proof-in-the-path A/B on the daily-brief email (proof-in-path-sharpened-honesty-inversion-0909): a
 * one-line Engine-A measured-edge insert + a conversion CTA for NON-SUBSCRIBERS (subscribers never get an
 * ask — subscriber-nudge-policy). FALSE (default) = the current no-proof brief for everyone (ships dark).
 * When true, the daily-brief-proof holdout (DAILY_BRIEF_PROOF_HOLDOUT_PCT) splits treatment vs control.
 */
export const DAILY_BRIEF_PROOF_ENABLED = (process.env.DAILY_BRIEF_PROOF_ENABLED ?? 'false') === 'true'

/**
 * Send the Morning Brief to anonymous EMAIL LEADS too (register email-leads-promised-a-daily-email-never-sent-1103).
 * InlineEmailCapture, on 45 pages, promises "Free daily email of the top plays ... No account needed" and writes
 * to email_leads, but no job ever mailed that table: email_lead_campaign_log held 0 rows on 2026-11-03 against 43
 * live leads. When true, send-daily-brief.ts runs a second pass over leads (not unsubscribed, deliverable, no
 * account under the same email) with the lead's own token unsubscribe link, exactly-once per ET day.
 * Default false: it mails real people, so the owner arms it.
 */
export const DAILY_BRIEF_LEADS_ENABLED = (process.env.DAILY_BRIEF_LEADS_ENABLED ?? 'false') === 'true'

/**
 * Proof-in-the-path on the WIN-BACK email (reactivation-email-blank-proof-and-win-hook-0905): the cancel
 * email currently shows zero proof. When true, it carries the honest Engine-A measured-edge one-liner (a
 * reminder of what they are leaving). No holdout — win-back volume (cancels) is too low to A/B, so this
 * ships as a fix (proof beats blank, per the finding), gated dark. FALSE (default) = unchanged email.
 */
export const WINBACK_PROOF_ENABLED = (process.env.WINBACK_PROOF_ENABLED ?? 'false') === 'true'

/**
 * MONSTER RADAR — the customer-facing Watcher product (product-play Move 02 "The Draw",
 * product-play-signal-to-subscribers-mapped-0909). A page at /radar that surfaces the REAL Watcher
 * signal (what watcher_watchlist flagged today + how it moved since) as a live board.
 *
 * STRUCTURAL FREE/PAID SPLIT = TIME, not features: subscribers (+ premium-day/trial holders) see the
 * board LIVE; everyone else sees it DELAYED by MONSTER_RADAR_FREE_DELAY_MIN, with the freshest flags
 * shown only as a locked count. Free genuinely watches the radar work — just too late to act — which
 * is the honest upgrade argument. ⚠ SIGNAL ONLY: never the entry/stop/shares trade plan or P&L (that
 * would be investment advice); framed "we flag volatility, not a direction," with winners AND losers
 * on the board (not a curated winners reel).
 *
 * DEFAULT FALSE → the route 404s (notFound) and stays unlinked, so it ships dark; arm with an env
 * change + restart (no redeploy). The split keys on real subscription status (read-only), NOT on
 * SUBSCRIPTION_GATING_ENABLED, so it works whether or not sitewide gating is on.
 */
export const MONSTER_RADAR_ENABLED = (process.env.MONSTER_RADAR_ENABLED ?? 'false') === 'true'
export const MONSTER_RADAR_FREE_DELAY_MIN = Math.max(0, Math.floor(Number(process.env.MONSTER_RADAR_FREE_DELAY_MIN ?? 20)) || 0)

/**
 * REAL-TIME PICKS (product-play Move 03 "Delivery", [[request-realtime-alert-tier-demand]]). Pushes a
 * newly-armed Watcher pick to OPTED-IN SUBSCRIBERS the moment it fires (web push via notify()), as the
 * paid timeliness tier — the thing free/delayed radar users structurally cannot get. The delivery job
 * (src/scripts/realtime-pick-watcher.ts) no-ops while this is false, and the /radar opt-in toggle is
 * hidden, so it ships dark; arm with an env change + a systemd enable (no redeploy).
 *
 * ⚠ SIGNAL ONLY: the payload carries the volatility flag (ticker, signal strength, since-flag move) and
 * links to /radar — NEVER entry/stop/target/shares or P&L (that reads as investment advice). Gated to
 * real subscribers (is_subscriber OR premium_days_remaining>0), who carry no conversion ask.
 */
export const REALTIME_PICKS_ENABLED = (process.env.REALTIME_PICKS_ENABLED ?? 'false') === 'true'

/**
 * Default premium-consumption go-live date (ET, yyyy-MM-dd) — the launch day. Used by the
 * date-gated donation bonus below when `PREMIUM_GO_LIVE_DATE` env is unset. Keeping a hard default
 * here (rather than null) means the 2× pre-launch bonus auto-expires at launch even if nobody
 * touches the env. Owner can still override the date via `PREMIUM_GO_LIVE_DATE`.
 */
const DEFAULT_PREMIUM_GO_LIVE_YMD = '2026-07-01'

/** Today's calendar day in America/New_York, yyyy-MM-dd. Self-contained (no date-fns) so this
 *  module stays import-light and safe to pull into client bundles. Evaluated LIVE per call. */
function todayEtYmd(now: Date = new Date()): string {
  // en-CA gives yyyy-MM-dd; timeZone pins it to Eastern regardless of server/browser locale.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

/**
 * Donation → premium-days conversion multiplier (the standing bonus rate), evaluated LIVE per call
 * (NOT a boot-time constant — that's the bug this fixes: a stale const meant the 2× bonus could end
 * a day early / lag the go-live flip). Resolution order:
 *   1. explicit env `DONATION_MULTIPLIER` (any value) → use it verbatim (owner override / rollback).
 *   2. otherwise DATE-GATE: 2× while today (America/New_York) is strictly BEFORE the go-live date;
 *      1× (standard) on/after it. The go-live date is `PREMIUM_GO_LIVE_DATE` env, else the launch
 *      default. Net: the pre-launch bonus turns off automatically at exactly go-live — no restart.
 */
export function donationMultiplier(now: Date = new Date()): number {
  const raw = (process.env.DONATION_MULTIPLIER ?? '').trim()
  if (raw !== '') {
    const n = Number(raw)
    if (Number.isFinite(n) && n > 0) return n
  }
  const goLive = PREMIUM_GO_LIVE_YMD ?? DEFAULT_PREMIUM_GO_LIVE_YMD
  return todayEtYmd(now) < goLive ? 2 : 1
}

/** True while the pre-launch donation bonus is active (multiplier > 1). Live per call. */
export function donationBonusActive(now: Date = new Date()): boolean {
  return donationMultiplier(now) > 1
}

/** Whole-number multiplier for copy, e.g. "2×". Live per call. */
export function donationMultiplierLabel(now: Date = new Date()): string {
  const m = donationMultiplier(now)
  return `${Number.isInteger(m) ? m : m.toFixed(1)}×`
}

/**
 * Referral / affiliate program master switch. Env-driven, DEFAULT FALSE — only
 * `REFERRAL_ENABLED=true` turns it on. While false: NO referral attribution, NO commission
 * accrual, the affiliate dashboard + partner page 404/redirect, and the live
 * subscription/access/billing path is completely untouched. Go-live / instant rollback is just
 * an env change + restart (no code change, no redeploy). DO NOT enable in prod yet.
 */
export const REFERRAL_ENABLED = (process.env.REFERRAL_ENABLED ?? 'false') === 'true'

/**
 * MEMBER referral loop master switch (distinct from the affiliate/commission program above).
 * Env-driven, DEFAULT FALSE. This is the two-sided "share your win, both get premium days" loop:
 * every member gets an invite link, and when an invited friend verifies their email, referrer and
 * invitee each get MEMBER_REFERRAL_REWARD_DAYS premium days (via topUpPremiumDays, NOT cash — it never
 * touches the unfinished Stripe Connect payout path). While false: the invite page + endpoint 404,
 * no reward is granted, and the live path is untouched. Deliberately SEPARATE from REFERRAL_ENABLED so
 * the member loop can run WITHOUT exposing the invite-only affiliate dashboard/commission machinery.
 * ⭐ NO LONGER A CONST (2026-10-20): read at request time by memberReferralEnabled() in
 * settings/runtimeFlags.ts, so the OWNER flips it on /admin/integrations with no deploy; .env.local
 * MEMBER_REFERRAL_ENABLED still works as the fallback. A const here would read stale once the page sets it.
 * Addresses request growth: wins-into-referrals; owner-integrations-page-1020.
 */

/** Premium days granted to EACH side (referrer + invitee) when an invited friend verifies. Tunable. */
export const MEMBER_REFERRAL_REWARD_DAYS = Number(process.env.MEMBER_REFERRAL_REWARD_DAYS ?? '14') || 14

/**
 * Attribution (the ddt_ref cookie + referred_by stamp at signup) must fire when EITHER program is on.
 * The affiliate COMMISSION path stays gated on REFERRAL_ENABLED + approval separately; this only widens
 * WHO gets attributed, which is harmless and required for the member loop to credit anyone.
 * ⭐ Now referralAttributionEnabled() in settings/runtimeFlags.ts (same rule, read at request time).
 */

/**
 * Email-verification master switch. Env-driven, DEFAULT FALSE — only `EMAIL_VERIFICATION_ENABLED=true`
 * turns it on. While false: signup behaves EXACTLY as today — no verification email is sent, and every
 * user (new or existing) is treated as verified, so the 5-day trial grant + premium access are
 * completely unaffected. When true: a NEW signup is created UNVERIFIED, is sent a Resend verification
 * email, and does NOT receive the trial grant or premium access until they click the verify link.
 * Existing users are grandfathered (backfilled email_verified=true) and are never gated.
 *
 * Go-live requires BOTH this flag AND a `RESEND_API_KEY`. Instant rollback is just an env change +
 * restart (no code change, no redeploy). DO NOT enable in prod until the Resend key is set.
 */
export const EMAIL_VERIFICATION_ENABLED = (process.env.EMAIL_VERIFICATION_ENABLED ?? 'false') === 'true'

/**
 * Recurring commission rate paid to a referrer on each referred subscriber's payment.
 * 0.20 = 20%. SINGLE-TIER: this is the ONLY referral reward — there is no bonus for recruiting
 * other affiliates (that would be an illegal pyramid). Env-overridable for tuning.
 */
export const REFERRAL_COMMISSION_RATE = Number(process.env.REFERRAL_COMMISSION_RATE ?? 0.20)

/**
 * TWO-TIER PARTNER PROGRAM (owner 2026-08-03). The tier is decided by the PARTNER'S OWN subscription
 * status at the moment a referred payment lands:
 *
 *   BASE (partner is not a subscriber)   10% cash for life  +  5 premium days
 *   PRO  (partner has an active sub)     20% cash for life  + 10 premium days
 *
 * ⭐ STILL SINGLE-TIER IN THE LEGAL SENSE — the two rates are about the PARTNER'S OWN subscription,
 * not about recruiting other partners. There is no downline and no override on anyone else's earnings,
 * which is the line between an affiliate program and an illegal pyramid.
 *
 * ⚠ THE RATE IS SNAPSHOTTED ONTO EACH COMMISSION ROW (`referral_commissions.rate`) at accrual time, so
 * changing these numbers NEVER rewrites what is already owed. A partner who subscribes today starts
 * earning 20% on the NEXT invoice; earlier rows keep the 10% they were accrued at. That is deliberate:
 * silently repricing money already promised is the one thing a partner program cannot do.
 */
export const REFERRAL_RATE_BASE = Number(process.env.REFERRAL_RATE_BASE ?? 0.10)
export const REFERRAL_RATE_SUBSCRIBER = Number(process.env.REFERRAL_RATE_SUBSCRIBER ?? 0.20)
export const REFERRAL_BONUS_DAYS_BASE = Number(process.env.REFERRAL_BONUS_DAYS_BASE ?? 5)
export const REFERRAL_BONUS_DAYS_SUBSCRIBER = Number(process.env.REFERRAL_BONUS_DAYS_SUBSCRIBER ?? 10)

/**
 * WIN-BACK OFFER (2026-09-06, winback-offer-flow-0906). A day after someone cancels, a gentle email
 * offers their first month back at $9.99. Modelled on the referral first-month coupon above.
 *
 * ⛔ OFF BY DEFAULT and DORMANT until BOTH are set: WINBACK_ENABLED=true AND a real Stripe coupon id in
 *    WINBACK_FIRST_MONTH_COUPON (the owner creates the coupon in Stripe). While off: the coupon is never
 *    applied and the scheduled sender no-ops (dry-run), so deploying this code sends nothing.
 * ⚠ The coupon should make ONLY the first invoice $9.99 (e.g. amount_off = standard - 9.99, duration:
 *    once), then revert to the standard price. Returning subscribers get no free trial, so it applies clean.
 */
export const WINBACK_ENABLED = (process.env.WINBACK_ENABLED ?? 'false') === 'true'
export const WINBACK_FIRST_MONTH_COUPON = (process.env.WINBACK_FIRST_MONTH_COUPON ?? '').trim()

/**
 * BALK-MOMENT DOWNSELL (2026-09-19, abandoned-checkout-offer-gap-0919 + checkout-completion-44pct...).
 * Checkout completion is ~44%; the 56% who abandon reach the $24.99/mo Stripe page and leave without a
 * card = a PRICE decision (willingness-to-pay-confirmed-at-checkout-0918). When such a balker RETURNS to
 * /subscribe, an in-app modal offers a fork: $9.99 first month (the WINBACK coupon, recurring) OR a
 * one-time $4.99/$9.99 pass (no subscription). Self-selecting: only a non-subscriber first-timer who
 * ALREADY has a Stripe customer (i.e. started checkout before) is eligible, so a committed subscriber
 * never sees it and the discount cannot be farmed by a fresh signup.
 *
 * ⛔ OFF BY DEFAULT. Dormant until DOWNSELL_ENABLED=true AND WINBACK_FIRST_MONTH_COUPON is set (reuses the
 *    live $9.99 winback coupon). While off: the eligibility API returns eligible:false (no modal) and the
 *    create-subscription downsell coupon path is skipped, so deploying this code changes nothing.
 */
export const DOWNSELL_ENABLED = (process.env.DOWNSELL_ENABLED ?? 'false') === 'true'

/**
 * Double-sided referral reward: the Stripe COUPON id applied to a referred new user's FIRST
 * subscription invoice (e.g. a 50%-off-once coupon → first month half price). Create the coupon in
 * Stripe (percent_off, duration: 'once') and set its id here. Empty = no new-user discount (the
 * referrer still earns their recurring commission). Only ever applied when REFERRAL_ENABLED.
 */
export const REFERRAL_FIRST_MONTH_COUPON = (process.env.REFERRAL_FIRST_MONTH_COUPON ?? '').trim()

/**
 * INTRO FIRST-MONTH PRICING (register: pricing-lever-prescope-0905). The "too expensive" lever, HELD
 * until the 09-15 holdback + 09-24 pass reads confirm price is the objection. Mirrors the referral
 * coupon, but applied to EVERY first-time subscriber (not just referred ones), so it is cannibalization-
 * safe: existing and past subscribers never see it.
 *   INTRO_FIRST_MONTH_ENABLED  turns it on (default false = fully dormant, no behaviour change).
 *   INTRO_FIRST_MONTH_COUPON   the Stripe coupon id applied to a first-time sub's FIRST invoice
 *                              (create it in Stripe: percent_off or amount_off, duration: 'once').
 *   INTRO_FIRST_MONTH_PRICE    the resulting first-month price as a DISPLAY string, e.g. '9.99', so
 *                              /subscribe can show "then $9.99 first month, then $24.99/mo". ⚠ It must
 *                              MATCH the coupon's real effect - the code cannot read a Stripe coupon's
 *                              value, so this label and the coupon are configured together.
 */
export const INTRO_FIRST_MONTH_ENABLED = (process.env.INTRO_FIRST_MONTH_ENABLED ?? 'false') === 'true'
export const INTRO_FIRST_MONTH_COUPON = (process.env.INTRO_FIRST_MONTH_COUPON ?? '').trim()
export const INTRO_FIRST_MONTH_PRICE = (process.env.INTRO_FIRST_MONTH_PRICE ?? '').trim()

/**
 * Live-chat dock starts CLOSED unless the user has explicitly opened it (register
 * chat-dock-opens-on-first-visit-and-hides-pulse-news-1102). Off = today: open on a first desktop visit. See
 * src/lib/chatDockDefault.ts for the measurements. Env-only, so arming and rollback need no code change.
 */
export const CHAT_DOCK_DEFAULT_CLOSED = (process.env.CHAT_DOCK_DEFAULT_CLOSED ?? 'false') === 'true'

/**
 * PRICE EXPERIMENT (register: request-lower-price-tier-demand, docs/price-experiment-spec-0928.md). A
 * TARGETED, reversible price test on tasted-non-payers: does a discount convert people who tried premium
 * and never paid? Distinct from INTRO above, which applies to EVERY first-timer. This one fires ONLY when
 * the request carries `priceexp:true` (the offer link the emailed TREATMENT arm receives, /subscribe?
 * offer=priceexp), so the holdout/control - who get a plain /subscribe link or no email - pay full price
 * and the causal read stays clean. Still re-verified server-side (first-time non-subscriber), so it can
 * never discount an existing/returning sub.
 *   PRICE_EXP_ENABLED   turns it on (default false = fully dormant, no behaviour change).
 *   PRICE_EXP_COUPON    the Stripe coupon id; the spec's offer is $14.99 x 3mo = percent_off 40,
 *                       duration 'repeating', duration_in_months 3 (owner creates it in Stripe).
 *   PRICE_EXP_PRICE     display string for the offer copy, e.g. '14.99', configured WITH the coupon
 *                       (the code cannot read a Stripe coupon's value).
 * ⛔ OFF BY DEFAULT and DORMANT until BOTH PRICE_EXP_ENABLED=true AND a real coupon id are set.
 */
export const PRICE_EXP_ENABLED = (process.env.PRICE_EXP_ENABLED ?? 'false') === 'true'
export const PRICE_EXP_COUPON = (process.env.PRICE_EXP_COUPON ?? '').trim()
export const PRICE_EXP_PRICE = (process.env.PRICE_EXP_PRICE ?? '').trim()

/**
 * Reserve / hold period (days) a commission stays in `held` before it becomes payout-eligible
 * (`accrued`). Covers the refund/chargeback window so we never pay out money that gets clawed
 * back. Env-overridable. Default 30.
 */
/**
 * ONE-TIME PREMIUM-DAY BONUS to the AFFILIATE, the first time a user they referred buys premium
 * (owner 2026-07-31: "i will pay them in premium time one time only for each subscriber. 10 days").
 *
 * This sits ALONGSIDE the 20% cash commission, it does not replace it: the affiliate earns both.
 * Granted once per REFERRED USER for all time, so a renewal, a second subscription or a Stripe retry
 * can never stack it. Set to 0 to switch the bonus off without touching code.
 */
export const REFERRAL_BONUS_DAYS = Number(process.env.REFERRAL_BONUS_DAYS ?? 10)

/**
 * PAYOUTS ARE OFF UNTIL BOTH THIS FLAG AND `--live` ARE SET. Two independent switches on purpose:
 * a cron that is accidentally enabled still cannot move money without the explicit argument, and a
 * hand-run with --live still cannot move money while the flag is false.
 */
export const REFERRAL_PAYOUT_ENABLED = (process.env.REFERRAL_PAYOUT_ENABLED ?? 'false') === 'true'

/** Minimum accrued balance before an affiliate is paid, so we do not wire out $0.80. */
export const REFERRAL_PAYOUT_MIN_CENTS = Math.round(Number(process.env.REFERRAL_PAYOUT_MIN_USD ?? 25) * 100)

export const REFERRAL_HOLD_DAYS = Number(process.env.REFERRAL_HOLD_DAYS ?? 30)

/**
 * Attribution cookie window (days). A `?ref=CODE` landing sets a first-party cookie; a signup
 * within this window is attributed to that code. Default 30.
 */
export const REFERRAL_COOKIE_DAYS = Number(process.env.REFERRAL_COOKIE_DAYS ?? 30)

/**
 * Premium-days CONSUMPTION go-live date (ET, yyyy-MM-dd). The day premium/trial days START to
 * burn. BEFORE this date NO day is ever consumed for anyone — balances are frozen ("not started
 * yet"), even on access. ON/AFTER it, the on-access trading-day burn begins. The owner sets this
 * to the day AFTER they flip the subscription switch, so nobody loses a day during the flip.
 *
 * Empty/unset → consumption is FROZEN for everyone (safe default: pre-launch nothing burns).
 * Set env `PREMIUM_GO_LIVE_DATE=2026-07-01` (etc.) at flip time. No code change / redeploy needed.
 */
export const PREMIUM_GO_LIVE_YMD: string | null = (() => {
  const raw = (process.env.PREMIUM_GO_LIVE_DATE ?? '').trim()
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null
})()

/** True if premium-day consumption is live for trading day `todayYmd` (yyyy-MM-dd, ET). */
export function isPremiumConsumptionLive(todayYmd: string, goLive: string | null = PREMIUM_GO_LIVE_YMD): boolean {
  if (!goLive) return false // unset → frozen (nothing burns until the owner sets the date)
  return todayYmd >= goLive
}

/**
 * Customer-facing phrase for what a donation grants — bonus-aware, drift-proof, LIVE per call.
 * Reads the date-gated `donationMultiplier()` so copy can never claim a bonus that has expired.
 */
export function donationCreditPhrase(now: Date = new Date()): string {
  return donationBonusActive(now)
    ? `${donationMultiplierLabel(now)} premium time (limited-time pre-launch bonus — ends at launch)`
    : 'premium time'
}

/**
 * Let a LOGGED-OUT visitor apply to the partner program with just an email, instead of having to
 * register first. Default OFF: it opens a public unauthenticated write path.
 *
 * 🚨 MEASURED 2026-09-10 (register: affiliate-traffic-arrived-conversion-untested-0910). /affiliate
 * now gets real traffic - 59 views / 46 sessions, up from 1 view EVER - but 35 of those 46 sessions
 * were logged out, and POST /api/referral/apply is 401 for anonymous. So 76% of the traffic could
 * not apply at all, and external applications are still 0.
 * ⛔ This is NOT shipped as "the gate was the problem". 35 sessions predicts 0.4-1.0 applications at
 * a normal signup rate, so the current 0 proves nothing. It removes a KNOWN structural step so the
 * next few hundred sessions measure the OFFER rather than the wall.
 */
export const REFERRAL_LEAD_APPLY_ENABLED = (process.env.REFERRAL_LEAD_APPLY_ENABLED ?? 'false') === 'true'
